import { Link } from '@tanstack/react-router';

import { AssigneePicker } from '@/features/identity';
import type { UserSummary } from '@/lib/api/generated/model';
import { Label } from '@/components/ui/label';
import { ProfileSummary } from '@/features/llm-profiles/components/ProfileSummary';
import type { LlmProfile } from '@/features/llm-profiles/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LEAVE_UNASSIGNED,
  outstanding,
  type Answers,
  type ReferenceRow,
} from '../upload/answers';

/**
 * A profile to offer. The name is enough to offer it; with what it runs on, the
 * option reads as the builder's picker does (spec 214 US4). Every caller passes
 * the full profiles, which carry both.
 */
export interface ProfileChoice extends Partial<
  Pick<LlmProfile, 'model' | 'effort' | 'max_turns' | 'description'>
> {
  slug: string;
  name: string;
}

/**
 * What is being brought in, in the words of the surface that is asking.
 *
 * The same questions are asked when a file is imported and when a library
 * workflow is installed, and the answers are identical; only the nouns differ.
 * A reader installing from the Library should not be told about importing a
 * file they never chose.
 */
export interface ResolveSubject {
  /** "this file", "this workflow" — used mid-sentence and capitalised. */
  noun: string;
  /** The past participle: "imported", "installed". */
  done: string;
  /** The bare verb: "import", "install". */
  verb: string;
}

const IMPORTING: ResolveSubject = {
  noun: 'this file',
  done: 'imported',
  verb: 'import',
};

function capitalised(noun: string) {
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

export interface ResolveReferencesProps {
  rows: readonly ReferenceRow[];
  answers: Answers;
  onAnswersChange: (next: Answers) => void;
  profiles: readonly ProfileChoice[];
  /** The profiles could not be read, which is not the same as there being none. */
  profilesUnavailable?: boolean;
  /** Whether this reader can go and make one, which changes the advice. */
  canCreateProfiles?: boolean;
  people: readonly UserSummary[];
  directoryUnavailable?: boolean;
  /** Defaults to importing a file, which is where these questions began. */
  subject?: ResolveSubject;
}

/**
 * What this file needs, and the controls to answer it (spec 102 US1, US3).
 *
 * Pure: it renders what it is given and reports changes upward. The dialog owns
 * the answers and the requests, which is what lets this be tested without a
 * query client and without a network (constitution, "components render, hooks
 * behave").
 *
 * **Nothing is pre-selected for a model reference, ever** — including when
 * exactly one profile exists, which is the case somebody will be tempted to
 * fill in helpfully. A workflow file names a profile, not a model, so one
 * candidate is not evidence that it is the right candidate; it is only evidence
 * that there is nothing to compare it against. A pre-filled picker is a
 * decision made for somebody that looks like a decision they made, and the
 * price of getting it wrong is work running on settings nobody chose (FR-012).
 *
 * A person starts answered, at "leave unassigned", because that is precisely
 * what happens today when an address matches nobody, and today's outcome has to
 * stay one press away (FR-008).
 */
export function ResolveReferences({
  rows,
  answers,
  onAnswersChange,
  profiles,
  profilesUnavailable = false,
  canCreateProfiles = false,
  people,
  directoryUnavailable = false,
  subject = IMPORTING,
}: ResolveReferencesProps) {
  const left = outstanding(rows, answers);

  return (
    <div className="flex flex-col gap-4">
      {/*
        Announced when it appears, because the number is the whole reason this
        screen interrupted somebody (FR-005). Polite: it is a statement about
        what they are looking at, not an interruption of it.
      */}
      <p
        role="status"
        aria-live="polite"
        className="text-muted-foreground text-xs"
      >
        {left.length === 0
          ? `Nothing left to answer. ${capitalised(subject.noun)} can be ${subject.done}.`
          : `${String(left.length)} ${left.length === 1 ? 'answer is' : 'answers are'} needed before ${subject.noun} can be ${subject.done}.`}
      </p>

      <div
        role="group"
        aria-label={`What ${subject.noun} needs`}
        className="flex max-h-72 flex-col gap-4 overflow-y-auto"
      >
        {rows.map((row) => (
          <Row
            key={row.key}
            row={row}
            answers={answers}
            onAnswersChange={onAnswersChange}
            profiles={profiles}
            profilesUnavailable={profilesUnavailable}
            canCreateProfiles={canCreateProfiles}
            subject={subject}
            people={people}
            directoryUnavailable={directoryUnavailable}
          />
        ))}
      </div>
    </div>
  );
}

/** Past this many, the step names are counted rather than listed. */
const STEPS_SHOWN = 2;

/**
 * Which steps a reference appears on, without burying the question in them.
 *
 * A workflow that names one model profile on every step listed every step —
 * five identifiers of thirty characters each, ahead of the control that
 * actually needs answering. The count is the fact; the names are there for
 * anyone who wants them.
 */
function StepsNamed({ stepIds }: { stepIds: readonly string[] }) {
  if (stepIds.length === 0) {
    return (
      <span className="text-muted-foreground">on a step with no name</span>
    );
  }
  if (stepIds.length <= STEPS_SHOWN) {
    return (
      <span className="text-muted-foreground">on {stepIds.join(', ')}</span>
    );
  }
  return (
    <details className="text-muted-foreground">
      <summary className="focus-visible:ring-ring cursor-pointer rounded-sm focus-visible:ring-3 focus-visible:outline-none">
        on {stepIds.length} steps
      </summary>
      <span className="mt-1 block break-words">{stepIds.join(', ')}</span>
    </details>
  );
}

function Row({
  row,
  answers,
  onAnswersChange,
  profiles,
  profilesUnavailable,
  canCreateProfiles,
  people,
  directoryUnavailable,
  subject = IMPORTING,
}: { row: ReferenceRow } & Omit<ResolveReferencesProps, 'rows'>) {
  // The person picker takes an id and expects a label to point at it, rather
  // than an aria-label of its own; the row's label is the natural one, and it
  // already names the reference (FR-025).
  const controlId = `resolve-${row.key}`;

  return (
    <div className="border-border flex flex-col gap-1.5 rounded-lg border p-3">
      <Label
        htmlFor={controlId}
        className="flex flex-wrap items-baseline gap-x-2 text-xs"
      >
        <span className="text-foreground font-medium">{row.value}</span>
        <StepsNamed stepIds={row.stepIds} />
        {/*
          In words, never by colour alone (FR-026), and stated rather than left
          to be discovered by pressing a control that does not respond.
        */}
        <span className="text-muted-foreground">
          {row.blocking
            ? `— needed to ${subject.verb}`
            : `— optional; leaving it will not prevent the ${subject.verb}`}
        </span>
      </Label>

      {row.kind === 'llm_profile' ? (
        <ProfileChoiceControl
          row={row}
          answers={answers}
          onAnswersChange={onAnswersChange}
          profiles={profiles}
          profilesUnavailable={profilesUnavailable}
          canCreateProfiles={canCreateProfiles}
          subject={subject}
        />
      ) : (
        <PersonChoiceControl
          controlId={controlId}
          row={row}
          answers={answers}
          onAnswersChange={onAnswersChange}
          people={people}
          directoryUnavailable={directoryUnavailable}
        />
      )}
    </div>
  );
}

function ProfileChoiceControl({
  row,
  answers,
  onAnswersChange,
  profiles,
  profilesUnavailable = false,
  canCreateProfiles = false,
  subject = IMPORTING,
}: {
  row: ReferenceRow;
  answers: Answers;
  onAnswersChange: (next: Answers) => void;
  profiles: readonly ProfileChoice[];
  profilesUnavailable?: boolean;
  canCreateProfiles?: boolean;
  subject?: ResolveSubject;
}) {
  if (profiles.length === 0) {
    /*
      The same words the builder's step inspector already uses, including its
      two endings. Two surfaces wording one state differently is how a reader
      learns to trust neither — and an empty picker would be the third way of
      saying it, the one that says nothing at all.
    */
    return (
      <p className="text-muted-foreground text-xs">
        {profilesUnavailable ? (
          'The LLM profiles could not be loaded, so nothing can be chosen here yet.'
        ) : canCreateProfiles ? (
          <>
            No LLM profiles exist yet.{' '}
            <Link to="/admin/harnesses" className="text-foreground underline">
              Create one
            </Link>{' '}
            before {subject.noun} can be {subject.done}.
          </>
        ) : (
          `No LLM profiles exist yet. Ask an organization admin to create one before ${subject.noun} can be ${subject.done}.`
        )}
      </p>
    );
  }

  const chosen = answers.profiles[row.value] ?? '';

  return (
    <Select
      value={chosen}
      onValueChange={(next: string | null) => {
        if (!next) return;
        onAnswersChange({
          ...answers,
          profiles: { ...answers.profiles, [row.value]: next },
        });
      }}
    >
      {/* Named after the reference it answers, never a bare "Choose" (FR-025). */}
      <SelectTrigger aria-label={`Settings to use instead of ${row.value}`}>
        <SelectValue>
          {(slug: string) =>
            slug
              ? (profiles.find((profile) => profile.slug === slug)?.name ??
                slug)
              : 'Choose settings to use instead'
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {profiles.map((profile) => (
          <SelectItem key={profile.slug} value={profile.slug}>
            {profile.model != null && profile.effort != null ? (
              <ProfileSummary
                profile={{
                  name: profile.name,
                  model: profile.model,
                  effort: profile.effort,
                  max_turns: profile.max_turns ?? null,
                  description: profile.description ?? null,
                }}
                variant="option"
              />
            ) : (
              profile.name
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function PersonChoiceControl({
  controlId,
  row,
  answers,
  onAnswersChange,
  people,
  directoryUnavailable = false,
}: {
  controlId: string;
  row: ReferenceRow;
  answers: Answers;
  onAnswersChange: (next: Answers) => void;
  people: readonly UserSummary[];
  directoryUnavailable?: boolean;
}) {
  const stepId = row.stepIds[0] ?? '';
  const current = answers.assignees[stepId];
  const chosenId =
    current && current !== LEAVE_UNASSIGNED
      ? (people.find((person) => person.email === current)?.id ?? null)
      : null;

  return (
    <AssigneePicker
      id={controlId}
      people={people}
      value={chosenId}
      directoryUnavailable={directoryUnavailable}
      // What choosing nothing *means* here: the gate is imported unassigned,
      // which is what would happen anyway. Naming the outcome rather than the
      // absence is the same argument the picker's own prop documents.
      emptyLabel="Leave unassigned"
      onChange={(userId) => {
        const email =
          userId === null
            ? LEAVE_UNASSIGNED
            : (people.find((person) => person.id === userId)?.email ??
              LEAVE_UNASSIGNED);
        onAnswersChange({
          ...answers,
          // The address, not the id: it is what the file used and what the
          // service takes.
          assignees: { ...answers.assignees, [stepId]: email },
        });
      }}
    />
  );
}
